import { useState, useMemo, useCallback, Fragment } from 'react';
import { Text, NumberInput, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { AbstractSingleFilterDropdownProps } from '../../SingleFilterType';
import { GenericFilterHeader } from '../GenericFilter/GenericFilterHeader';
import { GenericFilterBody } from '../GenericFilter/GenericFilterBody';
import { GenericFilterDropdown } from '../GenericFilter/GenericFilterDropdown';
import { GenericFilterFooter } from '../GenericFilter/GenericFilterFooter';
import { FilterItemRadio, FilterRadioContainer } from '../GenericSubElement/FilterItemRadio';
import type { RadioNumberFilterValueType } from './GenericRadioNumberType';
import { RadioNumberFilterOperation } from './GenericRadioNumberType';

const InputAndErrorContainer = styled.div`
  margin-left: ${tokens.spacer.x3};
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${tokens.spacer.x0};
  align-items: center;
`;

const ErrorContainer = styled.div`
  margin-top: ${tokens.spacer.x0};
`;

enum ValidationErrorEnum {
  SELECT_ONE_OPTION = 'Select an option',
  VALUE_1_IS_EMPTY = 'Please add a value',
  VALUE_2_IS_EMPTY = 'Please add a value ', // <= Hackish: I add a space to distinguish the 2
  VALUE_1_IS_NOT_A_NUMBER = 'Value is not a number',
  VALUE_2_IS_NOT_A_NUMBER = 'Value is not a number ', // <= Hackish: I add a space to distinguish the 2
}

export const readableRadioNumberOperations: Record<RadioNumberFilterOperation, string> = {
  [RadioNumberFilterOperation.IS_GREATER_THAN]: 'is greater than',
  [RadioNumberFilterOperation.IS_LESS_THAN]: 'is less than',
  [RadioNumberFilterOperation.IS_EXACTLY]: 'is exactly',
  [RadioNumberFilterOperation.IS_NOT]: 'is not',
  [RadioNumberFilterOperation.IS_BETWEEN]: 'is between',
  [RadioNumberFilterOperation.IS_UNKNOWN]: 'is unknown',
  [RadioNumberFilterOperation.HAS_ANY_VALUE]: 'has any value',
};

interface GenericRadioDropdownProps<T> extends AbstractSingleFilterDropdownProps<T> {
  options: RadioNumberFilterOperation[];
  placeholders?: Partial<Record<RadioNumberFilterOperation, string>>;
}

function getFilterValueErrors(
  operation?: RadioNumberFilterOperation,
  value1?: string,
  value2?: string,
): ValidationErrorEnum | undefined {
  if (!operation) {
    return ValidationErrorEnum.SELECT_ONE_OPTION;
  }
  if (
    operation === RadioNumberFilterOperation.IS_UNKNOWN ||
    operation === RadioNumberFilterOperation.HAS_ANY_VALUE
  ) {
    return undefined;
  }
  if (value1 == null || value1 === '') {
    return ValidationErrorEnum.VALUE_1_IS_EMPTY;
  }
  if (Number.isNaN(parseInt(value1, 10))) {
    return ValidationErrorEnum.VALUE_1_IS_NOT_A_NUMBER;
  }
  if (operation === RadioNumberFilterOperation.IS_BETWEEN && (value2 == null || value2 === '')) {
    return ValidationErrorEnum.VALUE_2_IS_EMPTY;
  }
  if (
    operation === RadioNumberFilterOperation.IS_BETWEEN &&
    value2 != null &&
    Number.isNaN(parseInt(value2, 10))
  ) {
    return ValidationErrorEnum.VALUE_2_IS_NOT_A_NUMBER;
  }
  return undefined;
}

function getStringifyValue1(value?: RadioNumberFilterValueType): string {
  if (!value) {
    return '';
  }
  if ('value' in value) {
    return `${value.value}`;
  }
  if ('from' in value) {
    return `${value.from}`;
  }
  return '';
}

function getStringifyValue2(value?: RadioNumberFilterValueType): string {
  if (!value) {
    return '';
  }
  if ('to' in value) {
    return `${value.to}`;
  }
  return '';
}

const DEFAULT_PLACEHOLDER = 'Add a number...';

export function GenericRadioNumberDropdown({
  currentValue,
  optionalDefaultValue,
  onApply,
  options = [],
  filterName,
  placeholders,
  header,
}: GenericRadioDropdownProps<RadioNumberFilterValueType>): JSX.Element {
  const initialValue = useMemo(
    () => currentValue ?? optionalDefaultValue,
    [currentValue, optionalDefaultValue],
  );
  const [dirtyOperation, setDirtyOperation] = useState<RadioNumberFilterOperation | undefined>(
    initialValue?.operation ?? options.find(() => true),
  );
  // We handle the value as string to be able to check for empty string and correctness errors
  const [dirtyStringifyValue1, setDirtyStringifyValue1] = useState(
    getStringifyValue1(initialValue),
  );
  const [dirtyStringifyValue2, setDirtyStringifyValue2] = useState(
    getStringifyValue2(initialValue),
  );

  const [validationError, setValidationError] = useState<ValidationErrorEnum>();
  const onOperationChange = useCallback(
    (newOperation: RadioNumberFilterOperation) => {
      setDirtyOperation(newOperation);
      setValidationError(undefined);
    },
    [setDirtyOperation, setValidationError],
  );

  const onFilterValue1Change = useCallback(
    (newValue: string) => {
      setDirtyStringifyValue1(newValue);
      setValidationError(undefined);
    },
    [setDirtyStringifyValue1, setValidationError],
  );

  const onFilterValue2Change = useCallback(
    (newValue: string) => {
      setDirtyStringifyValue2(newValue);
      setValidationError(undefined);
    },
    [setDirtyStringifyValue2, setValidationError],
  );

  const handleApply = useCallback(() => {
    const trimmedDirtyValue1 = dirtyStringifyValue1?.trim();
    const trimmedDirtyValue2 = dirtyStringifyValue2?.trim();
    const filterValueErrors = getFilterValueErrors(
      dirtyOperation,
      trimmedDirtyValue1,
      trimmedDirtyValue2,
    );
    if (filterValueErrors) {
      setValidationError(filterValueErrors);
      setDirtyStringifyValue1(trimmedDirtyValue1);
      setDirtyStringifyValue2(trimmedDirtyValue2);
    } else if (!dirtyOperation) {
      onApply(undefined);
    } else if (dirtyOperation === RadioNumberFilterOperation.IS_BETWEEN) {
      const finalValue1 = parseInt(trimmedDirtyValue1, 10);
      const finalValue2 = parseInt(trimmedDirtyValue2, 10);
      onApply({
        operation: dirtyOperation,
        from: Math.min(finalValue1, finalValue2),
        to: Math.max(finalValue1, finalValue2),
      });
    } else if (
      dirtyOperation === RadioNumberFilterOperation.IS_UNKNOWN ||
      dirtyOperation === RadioNumberFilterOperation.HAS_ANY_VALUE
    ) {
      onApply({
        operation: dirtyOperation,
      });
    } else {
      onApply({
        operation: dirtyOperation,
        value: parseInt(trimmedDirtyValue1, 10),
      });
    }
  }, [onApply, dirtyOperation, dirtyStringifyValue1, dirtyStringifyValue2]);

  return (
    <GenericFilterDropdown ariaLabel={`${filterName} dropdown`} onApply={handleApply}>
      {header != null && <GenericFilterHeader>{header}</GenericFilterHeader>}
      <GenericFilterBody>
        <FilterRadioContainer>
          {options.map((option) => {
            return (
              <Fragment key={option}>
                <FilterItemRadio
                  isChecked={option === dirtyOperation}
                  id={`${filterName}-dropdown-${option}`}
                  name={option}
                  onChange={() => onOperationChange(option)}
                  value={`${option}`}
                  labelContent={readableRadioNumberOperations[option]}
                  isInvalid={validationError === ValidationErrorEnum.SELECT_ONE_OPTION}
                />
                {dirtyOperation === option &&
                  [
                    RadioNumberFilterOperation.IS_GREATER_THAN,
                    RadioNumberFilterOperation.IS_LESS_THAN,
                    RadioNumberFilterOperation.IS_EXACTLY,
                    RadioNumberFilterOperation.IS_NOT,
                  ].includes(dirtyOperation) && (
                    <InputAndErrorContainer>
                      <NumberInput
                        placeholder={
                          placeholders && placeholders[option] != null
                            ? placeholders[option]
                            : DEFAULT_PLACEHOLDER
                        }
                        value={dirtyStringifyValue1}
                        onChange={(e) => onFilterValue1Change(e.target.value)}
                        isInvalid={
                          validationError === ValidationErrorEnum.VALUE_1_IS_EMPTY ||
                          validationError === ValidationErrorEnum.VALUE_1_IS_NOT_A_NUMBER
                        }
                        size="s"
                        role="textbox"
                      />
                      {(validationError === ValidationErrorEnum.VALUE_1_IS_EMPTY ||
                        validationError === ValidationErrorEnum.VALUE_1_IS_NOT_A_NUMBER) && (
                        <ErrorContainer>
                          <Text textColor="text_error">{validationError}</Text>
                        </ErrorContainer>
                      )}
                    </InputAndErrorContainer>
                  )}
                {dirtyOperation === option &&
                  dirtyOperation === RadioNumberFilterOperation.IS_BETWEEN && (
                    <InputAndErrorContainer>
                      <InputContainer>
                        <div style={{ width: '44%' }}>
                          <NumberInput
                            placeholder={
                              placeholders && placeholders[option] != null
                                ? placeholders[option]
                                : DEFAULT_PLACEHOLDER
                            }
                            value={dirtyStringifyValue1}
                            onChange={(e) => onFilterValue1Change(e.target.value)}
                            isInvalid={
                              validationError === ValidationErrorEnum.VALUE_1_IS_EMPTY ||
                              validationError === ValidationErrorEnum.VALUE_1_IS_NOT_A_NUMBER
                            }
                            size="s"
                            role="textbox"
                          />
                        </div>
                        <div>and</div>
                        <div style={{ width: '44%' }}>
                          <NumberInput
                            placeholder={
                              placeholders && placeholders[option] != null
                                ? placeholders[option]
                                : DEFAULT_PLACEHOLDER
                            }
                            value={dirtyStringifyValue2}
                            onChange={(e) => onFilterValue2Change(e.target.value)}
                            isInvalid={
                              validationError === ValidationErrorEnum.VALUE_2_IS_EMPTY ||
                              validationError === ValidationErrorEnum.VALUE_2_IS_NOT_A_NUMBER
                            }
                            size="s"
                            role="textbox"
                          />
                        </div>
                      </InputContainer>
                      {(validationError === ValidationErrorEnum.VALUE_1_IS_EMPTY ||
                        validationError === ValidationErrorEnum.VALUE_1_IS_NOT_A_NUMBER ||
                        validationError === ValidationErrorEnum.VALUE_2_IS_EMPTY ||
                        validationError === ValidationErrorEnum.VALUE_2_IS_NOT_A_NUMBER) && (
                        <ErrorContainer>
                          <Text textColor="text_error">{validationError}</Text>
                        </ErrorContainer>
                      )}
                    </InputAndErrorContainer>
                  )}
              </Fragment>
            );
          })}
          {validationError === ValidationErrorEnum.SELECT_ONE_OPTION && (
            <Text textColor="text_error">{validationError}</Text>
          )}
        </FilterRadioContainer>
      </GenericFilterBody>
      <GenericFilterFooter />
    </GenericFilterDropdown>
  );
}
